/*
 * Copyright 2026 Sony Corporation
 */
import { z } from 'zod';

/** The app process on the device, as its agent reports it. */
export const AppStatusSchema = z.object({
  version: z.number().int().nonnegative(),
  state: z.enum(['running', 'stopped', 'crashed']),
  since: z.string().datetime().optional(),
  error: z.string().max(2000).optional(),
});
export type AppStatus = z.infer<typeof AppStatusSchema>;

/** One camera session of the app, keyed by the camera's address; an app that talks to several reports each. */
export const CameraStatusSchema = z.object({
  address: z.string().min(1).max(253),
  /**
   * needs_login: the camera takes PTP only through SSH ([Access Authen.] On) and the runtime has no login for it;
   * login_failed: it refused the login, or its SSH fingerprint changed. Neither is retried until the login changes.
   */
  state: z.enum(['connecting', 'connected', 'recovering', 'disconnected', 'failed', 'needs_login', 'login_failed']),
  model: z.string().max(80).optional(),
  since: z.string().datetime().optional(),
  error: z.string().max(2000).optional(),
  /** How the session reaches the camera once connected: plain PTP/IP, or PTP/IP inside SSH. */
  transport: z.enum(['tcp', 'ssh']).optional(),
  /** The camera's SSH host key ("SHA256:..."), to compare with [Access Authen. Info] on the camera. */
  fingerprint: z.string().max(100).optional(),
});
export type CameraStatus = z.infer<typeof CameraStatusSchema>;

/** The camera models a runtime can be set up for; the app-generation assistant takes them as a hint. */
export const CAMERA_MODELS = [
  'ILCE-7M5',
  'ILCE-7RM6',
  'ILCE-1M2',
  'ILCE-1',
  'ILCE-9M3',
  'ILCE-9M2',
  'ILCE-7RM5',
  'ILCE-7RM4A',
  'ILCE-7RM4',
  'ILCE-7M4',
  'ILCE-7SM3',
  'ILCE-7CM2',
  'ILCE-7CR',
  'ILCE-7C',
  'ILCE-6700',
  'ILX-LR1',
  'ILME-FX3',
  'ILME-FX3A',
  'ILME-FX30',
  'ILME-FX2',
  'ZV-E1',
  'ZV-E10',
  'ZV-E10M2',
  'ZV-1M2',
  'ZV-1',
  'ZV-1A',
  'ZV-1F',
  'DSC-RX1RM3',
  'DSC-RX100M7',
  'DSC-RX100M7A',
  'DSC-RX0M2',
  'ILME-FX6',
  'MPC-2610',
  'ILME-FR7',
  'BRC-AM7',
  'PXW-Z300',
  'PXW-Z200',
  'HXR-NX800',
] as const;
export const CameraModelSchema = z.enum(CAMERA_MODELS);
export type CameraModel = z.infer<typeof CameraModelSchema>;

/** The operating system of the PC a runtime is; apps and the agent are built for it. */
export const PlatformSchema = z.enum(['linux', 'windows']);
export type Platform = z.infer<typeof PlatformSchema>;

/** A host name or an IPv4 address; the port is the PTP/IP one and is not part of the setting. */
export const CameraAddressSchema = z
  .string()
  .trim()
  .max(253)
  .regex(/^[A-Za-z0-9]([A-Za-z0-9.-]*[A-Za-z0-9])?$/, 'Enter a host name or an IP address, without a port.');

export const MAX_CAMERA_ADDRESSES = 8;

/**
 * Models whose Help Guide has [Access Authen.] (PTP over SSH with a user and password); the runtime form opens the
 * login fields for them. A camera of another model can have it too: the app then reports needs_login.
 */
export const ACCESS_AUTH_MODELS: readonly string[] = [
  'ILCE-7M5',
  'ILCE-7RM5',
  'ILCE-7M4',
  'ILCE-9M3',
  'ILCE-7CM2',
  'ILCE-6700',
  'ZV-E1',
  'ZV-E10M2',
  'ILX-LR1',
];

/** The key of a runtime's login that is used for every camera without its own. */
export const DEFAULT_CAMERA_LOGIN = '*';

/** The camera's own rules for [Access Authen. Settings] (Help Guide), so a typo shows before it reaches the camera. */
export const CameraLoginUserSchema = z
  .string()
  .min(1, 'Enter the user shown in [Access Authen. Info].')
  .max(16, 'The camera allows at most 16 characters.')
  .regex(/^[A-Za-z0-9!%.=_]+$/, 'The camera allows letters, numbers and ! % . = _ only.');
export const CameraLoginPasswordSchema = z
  .string()
  .min(8, 'The camera requires 8 to 16 characters.')
  .max(16, 'The camera requires 8 to 16 characters.')
  .regex(/^[A-Za-z0-9!%.=_+,-]+$/, 'The camera allows letters, numbers and ! % . = _ - + , only.');
export const FingerprintSchema = z
  .string()
  .regex(/^SHA256:[A-Za-z0-9+/]{43}=?$/, 'A fingerprint looks like SHA256: followed by 43 characters.');

/**
 * A camera login as the API shows it: the user, whether a password is stored (it is never returned), and the SSH
 * fingerprint the camera must have, when one is set. Stored records ({ user, password_enc }) read as this too.
 */
export const CameraLoginSchema = z.preprocess(
  (value) =>
    value && typeof value === 'object' && 'password_enc' in value
      ? { ...value, has_password: Boolean((value as { password_enc?: unknown }).password_enc) }
      : value,
  z.object({
    user: z.string(),
    has_password: z.boolean().default(false),
    fingerprint: z.string().optional(),
  })
);
export type CameraLogin = z.infer<typeof CameraLoginSchema>;

/** A login as the user enters it. No password keeps the stored one; null removes the login. */
export const CameraLoginInputSchema = z
  .object({
    user: CameraLoginUserSchema,
    password: CameraLoginPasswordSchema.optional(),
    fingerprint: FingerprintSchema.nullable().optional(),
  })
  .strict();
export type CameraLoginInput = z.infer<typeof CameraLoginInputSchema>;

const CameraLoginKeySchema = z.union([z.literal(DEFAULT_CAMERA_LOGIN), CameraAddressSchema]);
const CameraLoginsInputSchema = z.record(CameraLoginKeySchema, CameraLoginInputSchema.nullable());
const CameraAddressesSchema = z
  .array(CameraAddressSchema)
  .max(MAX_CAMERA_ADDRESSES)
  .refine((list) => new Set(list).size === list.length, 'Each camera address only once.');

/**
 * A runtime: a PC that runs the agent and the apps, with the cameras the apps talk to. The API calls it a device.
 */
export const DeviceSchema = z.object({
  device_id: z.string().uuid(),
  name: z.string().min(1).max(80),
  platform: PlatformSchema.default('linux'),
  /** The camera models the runtime is meant for, at least one for runtimes made since models were chosen. */
  camera_models: z.array(z.string().min(1)).default([]),
  status: z.enum(['online', 'offline']),
  last_seen: z.string().datetime().nullable(),
  agent_version: z.string().min(1).nullable(),
  /** The version the agent last reported running; 0 or null when nothing has been delivered. */
  running_version: z.number().int().nonnegative().nullable().default(null),
  /** The format of the credentials last issued; null for a device whose credentials predate the record. */
  credentials_version: z.number().int().nonnegative().nullable().default(null),
  /** The cameras the runtime's apps connect to, the first by default; empty leaves it to the app to ask. */
  camera_addresses: z.array(z.string()).default([]),
  /**
   * Logins of cameras with access authentication, by address, and "*" for every other camera. Only the user and
   * whether a password is set are shown; the password never leaves the service.
   */
  camera_logins: z.record(z.string(), CameraLoginSchema).default({}),
  /** The machine the agent runs on, from its last presence message. */
  hostname: z.string().nullable().default(null),
  ip: z.string().nullable().default(null),
  /** The app and its cameras while the device is online; null and empty once it went offline. */
  app: AppStatusSchema.nullable().default(null),
  cameras: z.array(CameraStatusSchema).default([]),
  created_at: z.string().datetime(),
});
export type Device = z.infer<typeof DeviceSchema>;

/** The credentials format the current agent needs; devices below it must have their credentials issued again. */
export const CURRENT_CREDENTIALS_VERSION = 2;

export const UpdateDeviceRequestSchema = z
  .object({
    camera_addresses: CameraAddressesSchema,
    /** Logins to add, change (no password keeps the stored one) or remove (null); others stay as they are. */
    camera_logins: CameraLoginsInputSchema.optional(),
    /** Cameras whose remembered SSH fingerprint is dropped (reset or replaced), so the next login takes the new one. */
    forget_fingerprints: z.array(z.string().min(1).max(253)).max(MAX_CAMERA_ADDRESSES).optional(),
  })
  .strict();
export type UpdateDeviceRequest = z.infer<typeof UpdateDeviceRequestSchema>;

export const CreateDeviceRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    platform: PlatformSchema,
    camera_models: z
      .array(CameraModelSchema)
      .min(1, 'Choose at least one camera model.')
      .max(CAMERA_MODELS.length)
      .transform((models) => [...new Set(models)]),
    camera_addresses: CameraAddressesSchema.default([]),
    camera_logins: CameraLoginsInputSchema.optional(),
  })
  .strict();
export type CreateDeviceRequest = z.input<typeof CreateDeviceRequestSchema>;

export const ListDevicesResponseSchema = z.object({ devices: z.array(DeviceSchema) });
export type ListDevicesResponse = z.infer<typeof ListDevicesResponseSchema>;

/**
 * A short-lived link to the agent package (the binary with its install scripts, without credentials); the agent is
 * public software, so only the link expires.
 */
export const AgentDownloadResponseSchema = z.object({
  url: z.string().url(),
  os: z.string(),
  arch: z.string(),
});
export type AgentDownloadResponse = z.infer<typeof AgentDownloadResponseSchema>;

export const AgentDownloadQuerySchema = z.object({
  os: PlatformSchema.default('linux'),
  arch: z.enum(['amd64', 'arm64']).default('amd64'),
});
